import { CommonModule } from '@angular/common';
import { HttpClient, HttpEventType, HttpHeaders, HttpResponse } from '@angular/common/http';
import { Component, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ApiService, showToast } from './api.service';

type PdfQuality = 'screen' | 'ebook' | 'printer';
type ProgressPhase = 'idle' | 'uploading' | 'compressing' | 'ready';
const PDF_COMPRESS_MAX_BYTES = 120 * 1024 * 1024;
const PDF_COMPRESS_MAX_LABEL = '120 MB';

@Component({
  selector: 'app-admin-pdf-compressor',
  standalone: true,
  imports: [CommonModule, FormsModule],
  template: `
    <div class="pdf-tool">
      <header class="tool-header">
        <div>
          <span>Document tools</span>
          <h1>Compress PDF</h1>
          <p>Upload a PDF, reduce its file size, and download the compressed copy.</p>
        </div>
      </header>

      <main class="tool-layout">
        <section class="upload-panel">
          <label class="drop-zone" [class.has-file]="selectedFile()" [class.is-busy]="isCompressing()">
            <input type="file" accept="application/pdf,.pdf" (change)="onFileSelected($event)" [disabled]="isCompressing()">
            <i class="ti ti-file-type-pdf"></i>
            <strong>{{ selectedFile()?.name || 'Choose PDF' }}</strong>
            <small>{{ selectedFile() ? fileSizeLabel(selectedFile()!.size) : 'PDF files up to 120 MB' }}</small>
          </label>

          <div class="limit-note">
            <i class="ti ti-info-circle"></i>
            <span>Maximum upload size is 120 MB on this server. Larger PDFs must be reduced before uploading.</span>
          </div>

          <div class="quality-row">
            <button type="button" [class.active]="quality() === 'screen'" (click)="quality.set('screen')" [disabled]="isCompressing()">Smallest</button>
            <button type="button" [class.active]="quality() === 'ebook'" (click)="quality.set('ebook')" [disabled]="isCompressing()">Balanced</button>
            <button type="button" [class.active]="quality() === 'printer'" (click)="quality.set('printer')" [disabled]="isCompressing()">Clear Print</button>
          </div>

          <button class="btn-compress" type="button" (click)="compressPdf()" [disabled]="!selectedFile() || isCompressing()">
            <i class="ti" [class.ti-loader-2]="isCompressing()" [class.animate-spin]="isCompressing()" [class.ti-file-zip]="!isCompressing()"></i>
            {{ isCompressing() ? 'Compressing...' : 'Compress PDF' }}
          </button>

          <div class="progress-card" *ngIf="progressPhase() !== 'idle'">
            <div class="progress-head">
              <span>{{ progressTitle() }}</span>
              <strong>{{ activeProgress() }}%</strong>
            </div>
            <div class="progress-track">
              <div class="progress-fill" [style.width.%]="activeProgress()" [class.is-estimate]="progressPhase() === 'compressing'"></div>
            </div>
            <p>{{ progressText() }}</p>
          </div>
        </section>

        <section class="result-panel">
          <div class="metric-card">
            <span>Original</span>
            <strong>{{ originalSizeLabel() }}</strong>
          </div>
          <div class="metric-card">
            <span>Compressed</span>
            <strong>{{ compressedSizeLabel() }}</strong>
          </div>
          <div class="metric-card">
            <span>Saved</span>
            <strong>{{ savedLabel() }}</strong>
          </div>

          <button class="btn-download" type="button" (click)="downloadLastResult()" [disabled]="!compressedBlobUrl()">
            <i class="ti ti-download"></i>
            Download compressed PDF
          </button>
        </section>
      </main>
    </div>
  `,
  styles: [`
    .pdf-tool { min-height: 100%; padding: 28px; background: #f4f8fb; color: #0f172a; }
    .tool-header { margin-bottom: 18px; }
    .tool-header span { color: #64748b; font-size: 11px; font-weight: 800; letter-spacing: 0.06em; text-transform: uppercase; }
    .tool-header h1 { margin: 4px 0; color: #0b344d; font-size: 23px; font-weight: 700; }
    .tool-header p { margin: 0; color: #64748b; font-size: 13px; }
    .tool-layout { display: grid; grid-template-columns: minmax(0, 1.2fr) minmax(280px, 0.8fr); gap: 18px; }
    .upload-panel, .result-panel { background: #fff; border: 1px solid #dbe7ef; border-radius: 8px; box-shadow: 0 8px 24px rgba(15, 23, 42, 0.04); padding: 18px; }
    .drop-zone { display: flex; min-height: 220px; border: 1px dashed #b7cad8; border-radius: 8px; background: #fbfdff; align-items: center; justify-content: center; flex-direction: column; gap: 10px; cursor: pointer; text-align: center; }
    .drop-zone.has-file { border-color: #2B9AD2; background: #f0f9ff; }
    .drop-zone.is-busy { cursor: wait; opacity: 0.72; }
    .drop-zone input { display: none; }
    .drop-zone i { font-size: 42px; color: #dc2626; }
    .drop-zone strong { color: #0b344d; font-size: 15px; max-width: min(460px, 100%); overflow-wrap: anywhere; }
    .drop-zone small { color: #64748b; font-size: 12px; }
    .limit-note { display: flex; align-items: flex-start; gap: 8px; margin-top: 12px; padding: 10px 12px; border: 1px solid #bfdbfe; border-radius: 7px; background: #eff6ff; color: #1e3a8a; font-size: 12px; line-height: 17px; font-weight: 600; }
    .limit-note i { margin-top: 1px; color: #2563eb; font-size: 16px; flex: 0 0 auto; }
    .quality-row { display: grid; grid-template-columns: repeat(3, 1fr); gap: 8px; margin-top: 14px; }
    button { border-radius: 7px; border: 1px solid transparent; cursor: pointer; font-size: 12px; font-weight: 700; }
    button:disabled { cursor: not-allowed; opacity: 0.55; }
    .quality-row button { min-height: 38px; background: #fff; border-color: #d8e2ea; color: #475569; }
    .quality-row button.active { background: #eaf5fa; border-color: #9bd3ec; color: #075985; }
    .btn-compress, .btn-download { width: 100%; min-height: 42px; margin-top: 14px; display: inline-flex; align-items: center; justify-content: center; gap: 8px; }
    .btn-compress { background: #0369a1; color: #fff; }
    .btn-download { background: #ecfdf5; border-color: #bbf7d0; color: #047857; }
    .progress-card { margin-top: 14px; border: 1px solid #dbe7ef; border-radius: 8px; padding: 12px; background: #fbfdff; }
    .progress-head { display: flex; align-items: center; justify-content: space-between; gap: 10px; color: #0b344d; font-size: 12px; font-weight: 800; }
    .progress-head strong { font-size: 13px; }
    .progress-track { height: 9px; overflow: hidden; margin-top: 9px; background: #e2e8f0; border-radius: 999px; }
    .progress-fill { height: 100%; min-width: 4px; border-radius: inherit; background: linear-gradient(90deg, #0284c7, #22c55e); transition: width 0.35s ease; }
    .progress-fill.is-estimate { background: linear-gradient(90deg, #2563eb, #06b6d4); }
    .progress-card p { margin: 8px 0 0; color: #64748b; font-size: 12px; line-height: 17px; }
    .result-panel { display: grid; gap: 12px; align-content: start; }
    .metric-card { border: 1px solid #e2e8f0; border-radius: 8px; padding: 14px; background: #fbfdff; }
    .metric-card span { display: block; color: #64748b; font-size: 10px; font-weight: 800; letter-spacing: 0.04em; text-transform: uppercase; }
    .metric-card strong { display: block; margin-top: 5px; color: #0b344d; font-size: 18px; }
    @media (max-width: 860px) { .pdf-tool { padding: 18px; } .tool-layout { grid-template-columns: 1fr; } .quality-row { grid-template-columns: 1fr; } }
  `]
})
export class AdminPdfCompressorComponent {
  private api = inject(ApiService);
  private http = inject(HttpClient);

  selectedFile = signal<File | null>(null);
  quality = signal<PdfQuality>('ebook');
  isCompressing = signal(false);
  originalSize = signal(0);
  compressedSize = signal(0);
  compressedBlobUrl = signal('');
  compressedFilename = signal('compressed.pdf');
  progressPhase = signal<ProgressPhase>('idle');
  uploadProgress = signal(0);
  compressionProgress = signal(0);
  progressText = signal('');
  estimatedCompressionSeconds = signal(0);

  originalSizeLabel = computed(() => this.originalSize() ? this.fileSizeLabel(this.originalSize()) : '-');
  compressedSizeLabel = computed(() => this.compressedSize() ? this.fileSizeLabel(this.compressedSize()) : '-');
  activeProgress = computed(() => this.progressPhase() === 'uploading' ? this.uploadProgress() : this.compressionProgress());
  progressTitle = computed(() => {
    if (this.progressPhase() === 'uploading') return 'Uploading PDF';
    if (this.progressPhase() === 'compressing') return 'Compressing PDF';
    if (this.progressPhase() === 'ready') return 'Ready to download';
    return 'Preparing';
  });
  savedLabel = computed(() => {
    const original = this.originalSize();
    const compressed = this.compressedSize();
    if (!original || !compressed) return '-';
    const saved = Math.max(0, original - compressed);
    const percent = original > 0 ? Math.round((saved / original) * 100) : 0;
    return `${this.fileSizeLabel(saved)} (${percent}%)`;
  });

  private uploadStartedAt = 0;
  private compressionStartedAt = 0;
  private progressTimer: ReturnType<typeof setInterval> | null = null;

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0] || null;
    if (!file) return;
    if (file.type !== 'application/pdf' && !file.name.toLowerCase().endsWith('.pdf')) {
      showToast('error', 'Please choose a PDF file.');
      input.value = '';
      return;
    }
    if (file.size > PDF_COMPRESS_MAX_BYTES) {
      showToast('error', `Please choose a PDF under ${PDF_COMPRESS_MAX_LABEL}.`);
      input.value = '';
      return;
    }
    this.clearResult();
    this.selectedFile.set(file);
    this.originalSize.set(file.size);
  }

  compressPdf(): void {
    const file = this.selectedFile();
    if (!file || this.isCompressing()) return;

    const formData = new FormData();
    formData.append('pdf', file, file.name);
    formData.append('quality', this.quality());

    const token = this.api.accessToken();
    const headers = new HttpHeaders({
      'Authorization': token ? `Bearer ${token}` : ''
    });

    this.clearResult();
    this.isCompressing.set(true);
    this.startProgress(file);
    this.http.post(`${this.api.baseUrl}/tools/compress_pdf.php`, formData, {
      headers,
      observe: 'events',
      reportProgress: true,
      responseType: 'blob'
    }).subscribe({
      next: (event) => {
        if (event.type === HttpEventType.UploadProgress) {
          const total = event.total || file.size || 1;
          const percent = Math.min(100, Math.round((event.loaded / total) * 100));
          this.uploadProgress.set(percent);
          if (percent >= 100) this.startCompressionPhase(file);
          return;
        }
        if (event.type === HttpEventType.Response) {
          this.isCompressing.set(false);
          this.finishProgress();
          this.handleCompressedResponse(event as HttpResponse<Blob>);
        }
      },
      error: (err) => {
        this.isCompressing.set(false);
        this.stopProgressTimer();
        this.progressPhase.set('idle');
        this.readBlobError(err);
      }
    });
  }

  downloadLastResult(): void {
    const url = this.compressedBlobUrl();
    if (!url) return;
    const link = document.createElement('a');
    link.href = url;
    link.download = this.compressedFilename();
    link.click();
  }

  fileSizeLabel(bytes: number): string {
    if (!bytes) return '0 KB';
    const units = ['B', 'KB', 'MB', 'GB'];
    let size = bytes;
    let unit = 0;
    while (size >= 1024 && unit < units.length - 1) {
      size /= 1024;
      unit++;
    }
    return `${size.toFixed(unit === 0 ? 0 : 1)} ${units[unit]}`;
  }

  private handleCompressedResponse(response: HttpResponse<Blob>): void {
    const blob = response.body;
    if (!blob) {
      showToast('error', 'The compressed PDF was not received.');
      return;
    }

    this.revokeBlobUrl();
    this.compressedSize.set(Number(response.headers.get('X-Compressed-Size') || blob.size || 0));
    this.compressedFilename.set(this.filenameFromHeader(response.headers.get('Content-Disposition')) || this.defaultFilename());
    this.compressedBlobUrl.set(URL.createObjectURL(blob));

    const status = response.headers.get('X-Compression-Status');
    showToast('success', status === 'already-optimized' ? 'This PDF was already optimized. Download is ready.' : 'PDF compressed. Download is ready.');
  }

  private readBlobError(err: any): void {
    const body = err?.error;
    if (body instanceof Blob) {
      body.text()
        .then(text => {
          try {
            const parsed = JSON.parse(text);
            showToast('error', parsed?.message || 'Could not compress PDF.');
          } catch {
            showToast('error', text?.trim() || 'Could not compress PDF.');
          }
        })
        .catch(() => showToast('error', 'Could not compress PDF.'));
      return;
    }
    showToast('error', err?.error?.message || 'Could not compress PDF.');
  }

  private filenameFromHeader(header: string | null): string {
    const match = /filename="?([^"]+)"?/i.exec(header || '');
    return match?.[1] || '';
  }

  private defaultFilename(): string {
    const base = (this.selectedFile()?.name || 'document.pdf').replace(/\.pdf$/i, '');
    return `${base}_compressed.pdf`;
  }

  private clearResult(): void {
    this.revokeBlobUrl();
    this.compressedSize.set(0);
    this.compressedFilename.set('compressed.pdf');
    this.progressPhase.set('idle');
    this.uploadProgress.set(0);
    this.compressionProgress.set(0);
    this.progressText.set('');
    this.stopProgressTimer();
  }

  private revokeBlobUrl(): void {
    const url = this.compressedBlobUrl();
    if (url) URL.revokeObjectURL(url);
    this.compressedBlobUrl.set('');
  }

  private startProgress(file: File): void {
    this.stopProgressTimer();
    this.uploadStartedAt = Date.now();
    this.compressionStartedAt = 0;
    this.uploadProgress.set(1);
    this.compressionProgress.set(0);
    this.estimatedCompressionSeconds.set(this.estimateCompressionSeconds(file));
    this.progressPhase.set('uploading');
    this.progressText.set('Preparing upload...');
    this.progressTimer = setInterval(() => this.updateProgressText(), 1000);
    this.updateProgressText();
  }

  private startCompressionPhase(file: File): void {
    if (this.progressPhase() === 'compressing') return;
    this.compressionStartedAt = Date.now();
    this.uploadProgress.set(100);
    this.compressionProgress.set(5);
    this.estimatedCompressionSeconds.set(this.estimateCompressionSeconds(file));
    this.progressPhase.set('compressing');
    this.updateProgressText();
  }

  private finishProgress(): void {
    this.uploadProgress.set(100);
    this.compressionProgress.set(100);
    this.progressPhase.set('ready');
    this.progressText.set('Compression complete. Download is ready.');
    this.stopProgressTimer();
  }

  private updateProgressText(): void {
    if (this.progressPhase() === 'uploading') {
      const percent = Math.max(1, this.uploadProgress());
      const elapsed = Math.max(1, Math.round((Date.now() - this.uploadStartedAt) / 1000));
      const remaining = percent > 1 ? Math.max(1, Math.round((elapsed / percent) * (100 - percent))) : 0;
      this.progressText.set(percent < 100 ? `Uploading ${percent}% - about ${this.timeLabel(remaining)} left.` : 'Upload complete. Starting compression...');
      return;
    }

    if (this.progressPhase() === 'compressing') {
      const elapsed = Math.max(1, Math.round((Date.now() - this.compressionStartedAt) / 1000));
      const estimate = Math.max(1, this.estimatedCompressionSeconds());
      const estimatedProgress = Math.min(95, Math.max(5, Math.round((elapsed / estimate) * 92)));
      this.compressionProgress.set(Math.max(this.compressionProgress(), estimatedProgress));
      const remaining = Math.max(0, estimate - elapsed);
      this.progressText.set(remaining > 0 ? `Compressing on server - about ${this.timeLabel(remaining)} left.` : 'Compressing on server - taking a little longer than estimated.');
    }
  }

  private estimateCompressionSeconds(file: File): number {
    const sizeMb = Math.max(1, file.size / (1024 * 1024));
    const qualityMultiplier = this.quality() === 'printer' ? 1.35 : (this.quality() === 'screen' ? 0.9 : 1.1);
    return Math.min(180, Math.max(8, Math.round((8 + sizeMb * 0.75) * qualityMultiplier)));
  }

  private timeLabel(seconds: number): string {
    if (seconds < 60) return `${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    const rest = seconds % 60;
    return rest ? `${minutes}m ${rest}s` : `${minutes}m`;
  }

  private stopProgressTimer(): void {
    if (this.progressTimer) {
      clearInterval(this.progressTimer);
      this.progressTimer = null;
    }
  }
}
